/**
 * Kern-mutatie logica voor de Firestore 'tasks' collectie.
 */
// @ts-check
import {
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  collection,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { applyDurationReviewToUpdate } from '@/features/tasks/utils/data/durationReviewUtils';

/**
 * @param {string[] | undefined} arr
 * @param {string} fieldName
 * @param {any} task
 * @param {{ value: boolean }} needsWriteRef
 */
const normalizeStringArray = (arr, fieldName, task, needsWriteRef) => {
  if (!Array.isArray(arr)) return;
  const normalized = arr.map((item) =>
    typeof item === 'string' ? item.trim().toLowerCase() : item,
  );
  if (normalized.some((item, i) => item !== arr[i])) {
    needsWriteRef.value = true;
  }
  task[fieldName] = normalized;
};

/**
 * @param {any} task
 */
const convertTimestampFields = (task) => {
  const dateFields = ['createdAt', 'updatedAt'];
  dateFields.forEach((field) => {
    if (task[field] && typeof task[field].toDate === 'function') {
      task[field] = task[field].toDate();
    }
  });
};

/**
 * Converts a Firestore DocumentSnapshot into a plain task object.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnapshot
 * @returns {{ task: Task, needsWrite: boolean }}
 */
export const processDoc = (docSnapshot) => {
  const resolvedId = docSnapshot.id || crypto.randomUUID();
  const data = docSnapshot.data({ serverTimestamps: 'estimate' }) || {};
  /** @type {Task} */
  // A root task written without a `parentId` field hydrates as `undefined`.
  // Every sibling filter compares against `null`, so normalise here — the one
  // place every Firestore task passes through.
  const task = /** @type {any} */ ({
    ...data,
    id: resolvedId,
    parentId: data.parentId ?? null,
  });

  const needsWriteRef = { value: false };

  normalizeStringArray(task.projects, 'projects', task, needsWriteRef);
  normalizeStringArray(task.contexts, 'contexts', task, needsWriteRef);
  convertTimestampFields(task);

  return { task, needsWrite: needsWriteRef.value };
};

/**
 * Handles repairing tasks that need normalization or project inheritance.
 * @param {any[]} processedChanges
 * @param {Map<string, any>} snapTaskMap
 */
export const handleTaskRepairs = (processedChanges, snapTaskMap) => {
  const toWrite = processedChanges.filter((c) => {
    if (c.type === 'removed' || !c.task.parentId) return c.needsWrite;
    const parent = snapTaskMap.get(c.task.parentId);
    if (!parent?.projects?.length) return c.needsWrite;
    const parentProjects = parent.projects;
    const taskProjects = c.task.projects || [];
    const diverged = parentProjects.some((p) => !taskProjects.includes(p));
    if (diverged) {
      c.task.projects = [...parentProjects];
      c.needsWrite = true;
    }
    return c.needsWrite;
  });

  if (toWrite.length > 0) {
    const batch = writeBatch(db);
    toWrite.forEach(({ task, docRef }) => {
      batch.update(docRef, {
        projects: task.projects,
        contexts: task.contexts,
      });
    });
    batch
      .commit()
      .catch((err) =>
        console.error(
          '[taskMutations] Failed to write normalized projects/contexts to Firestore:',
          err,
        ),
      );
  }
};

/**
 * Adds a set operation to a batch, automatically including createdAt and updatedAt.
 * @param {import('firebase/firestore').WriteBatch} batch
 * @param {import('firebase/firestore').DocumentReference} docRef
 * @param {NewTask} data
 */
export const setTaskInBatch = (batch, docRef, data) => {
  batch.set(docRef, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Adds an update operation to a batch, automatically including updatedAt.
 * @param {import('firebase/firestore').WriteBatch} batch
 * @param {string} taskId
 * @param {UpdateTask} data
 */
export const updateTaskInBatch = (batch, taskId, data) => {
  const taskRef = doc(db, 'tasks', taskId);
  batch.update(taskRef, {
    ...applyDurationReviewToUpdate(data),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Updates a task document in Firestore.
 * @param {string} taskId
 * @param {UpdateTask} data
 */
export const updateTask = async (taskId, data) => {
  const taskRef = doc(db, 'tasks', taskId);
  const updateData = {
    ...applyDurationReviewToUpdate(data),
    updatedAt: serverTimestamp(),
  };
  await updateDoc(taskRef, updateData);
};

/**
 * Updates a task's duration fields.
 * @param {string} taskId
 * @param {number} duration
 */
export const updateTaskDuration = async (taskId, duration) => {
  const taskRef = doc(db, 'tasks', taskId);
  await updateDoc(taskRef, {
    duration: duration,
    estimatedDuration: duration,
    requireTimeReview: false,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Adds a new task document to Firestore.
 * @param {NewTask} taskData
 * @returns {Promise<string>} The new document ID.
 */
export const createTask = async (taskData) => {
  const tasksRef = collection(db, 'tasks');
  const docRef = await addDoc(tasksRef, {
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...taskData,
  });
  return docRef.id;
};

/**
 * Deletes multiple task documents from Firestore in a batch.
 * @param {string[]} taskIds
 */
export const deleteTasks = async (taskIds) => {
  if (!taskIds.length) return;
  const batch = writeBatch(db);
  taskIds.forEach((id) => {
    batch.delete(doc(db, 'tasks', id));
  });
  await batch.commit();
};

/**
 * Creates a new Firestore batch.
 * @returns {import('firebase/firestore').WriteBatch}
 */
export const getTaskBatch = () => writeBatch(db);

/**
 * Commits a Firestore batch.
 * @param {import('firebase/firestore').WriteBatch} batch
 */
export const commitBatch = (batch) => batch.commit();
